import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr, fdate } from "@/api";
import { PageHeader, SlabBadge } from "@/components/Shared";
import { DynamicFields } from "@/components/DynamicFields";
import { FileUploadField, fileUrl } from "@/components/FileUpload";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Contact, Eye, Trash2, User, X, FileText } from "lucide-react";

const EMPTY = {
  name: "", type: "b2b", category: "shopkeeper", gstin: "", phone: "", email: "",
  address: "", state: "", credit_days: 0, contact_person: "", pan: "", aadhaar: "",
  photo: "", aadhaar_doc: "", pan_doc: "", opening_balance: 0,
};

const Field = ({ label, value, mono }) => (
  <div>
    <p className="text-[10px] font-mono2 uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`text-sm text-slate-800 mt-0.5 ${mono ? "font-mono2" : ""}`}>{value || "—"}</p>
  </div>
);

const DocLink = ({ label, path }) => (
  <div>
    <p className="text-[10px] font-mono2 uppercase tracking-wider text-slate-500">{label}</p>
    {path ? (
      <a href={fileUrl(path)} target="_blank" rel="noreferrer" data-testid={`party-doc-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 mt-0.5 font-semibold">
        <FileText className="w-3.5 h-3.5" /> View document
      </a>
    ) : <p className="text-sm text-slate-800 mt-0.5">—</p>}
  </div>
);

export default function Parties() {
  const { user } = useAuth();
  const [tab, setTab] = useState("clients");
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selected, setSelected] = useState(null); // {type, id}
  const [ledger, setLedger] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selected) return setLedger(null);
    api.get(`/ledger/${selected.type === "client" ? "client" : "vendor"}/${selected.id}`)
      .then((r) => setLedger(r.data)).catch(() => {});
  }, [selected, clients, vendors]);

  const list = tab === "clients" ? clients : vendors;
  const party = selected
    ? (selected.type === "client" ? clients : vendors).find((p) => (selected.type === "client" ? p.client_id : p.vendor_id) === selected.id)
    : null;

  const openEdit = (p, type) => {
    setForm({
      name: p.name, type: p.type || "b2b", category: p.category || "shopkeeper",
      gstin: p.gstin || "", phone: p.phone || "", email: p.email || "", address: p.address || "",
      state: p.state || "", credit_days: p.credit_days || 0, contact_person: p.contact_person || "",
      pan: p.pan || "", aadhaar: p.aadhaar || "", photo: p.photo || "",
      aadhaar_doc: p.aadhaar_doc || "", pan_doc: p.pan_doc || "", opening_balance: p.opening_balance || 0,
    });
    setCustom(p.custom || {});
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = selected.type === "client" ? `/clients/${selected.id}` : `/vendors/${selected.id}`;
      const payload = selected.type === "client"
        ? { ...form, category: form.type === "b2b" ? form.category : null, custom }
        : { name: form.name, gstin: form.gstin, phone: form.phone, email: form.email, address: form.address, state: form.state, credit_days: form.credit_days, contact_person: form.contact_person, pan: form.pan, aadhaar: form.aadhaar, photo: form.photo, aadhaar_doc: form.aadhaar_doc, pan_doc: form.pan_doc, opening_balance: form.opening_balance, custom };
      await api.put(path, payload);
      toast.success("Profile updated");
      setOpen(false);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${party.name}? Their ledger history stays in the books.`)) return;
    try {
      await api.delete(selected.type === "client" ? `/clients/${selected.id}` : `/vendors/${selected.id}`);
      toast.success("Profile deleted");
      setSelected(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Delete failed");
    }
  };

  return (
    <div data-testid="parties-page">
      <PageHeader title="Parties Directory" sub="Every client and vendor in one place — click View for the complete profile" />

      <div className="flex rounded-full border border-slate-300 overflow-hidden w-max mb-6" data-testid="parties-tabs">
        {[["clients", "Clients"], ["vendors", "Vendors"]].map(([t, l]) => (
          <button key={t} onClick={() => { setTab(t); setSelected(null); }} data-testid={`parties-tab-${t}`}
            className={`px-6 py-2 text-sm font-semibold transition-colors duration-200 ${tab === t ? "bg-emerald-500 text-slate-950" : "text-slate-600 hover:text-slate-800"}`}>
            {l} ({t === "clients" ? clients.length : vendors.length})
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {list.map((p) => {
          const id = tab === "clients" ? p.client_id : p.vendor_id;
          return (
            <div key={id} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-emerald-500/40 transition-colors duration-200" data-testid={`party-card-${id}`}>
              <div className="flex items-center gap-3">
                {p.photo
                  ? <img src={fileUrl(p.photo)} alt="" className="w-11 h-11 rounded-xl object-cover border border-slate-300" />
                  : <span className="w-11 h-11 rounded-xl bg-emerald-500/12 border border-emerald-500/35 flex items-center justify-center"><User className="w-5 h-5 text-emerald-600" /></span>}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate">{p.name}</p>
                  <p className="text-xs text-slate-500 truncate">{p.gstin || (tab === "clients" ? "B2C" : "—")}</p>
                </div>
                {tab === "clients" && <SlabBadge slab={p.type === "b2b" ? p.category : "retail"} />}
              </div>
              <button
                onClick={() => setSelected({ type: tab === "clients" ? "client" : "vendor", id })}
                data-testid={`party-view-${id}`}
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-300 hover:border-emerald-500/50 text-slate-700 hover:text-emerald-700 text-xs font-semibold py-2 transition-colors duration-150">
                <Eye className="w-3.5 h-3.5" /> View Complete Profile
              </button>
            </div>
          );
        })}
      </div>

      {party && (
        <div className="rounded-2xl border border-emerald-500/30 bg-white/90 p-6 shadow-[0_0_35px_rgba(16,185,129,0.1)]" data-testid="party-profile-view">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              {party.photo
                ? <img src={fileUrl(party.photo)} alt="" className="w-16 h-16 rounded-2xl object-cover border border-emerald-500/40" data-testid="party-profile-photo" />
                : <span className="w-16 h-16 rounded-2xl bg-emerald-500/12 border border-emerald-500/35 flex items-center justify-center text-emerald-600 font-bold text-2xl">{party.name[0]}</span>}
              <div>
                <h2 className="text-xl font-bold text-slate-900" data-testid="party-profile-name">{party.name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full border text-[11px] font-mono2 uppercase ${selected.type === "client" ? "border-blue-500/40 text-blue-600 bg-blue-500/10" : "border-amber-500/40 text-amber-600 bg-amber-500/10"}`}>
                    {selected.type}
                  </span>
                  {selected.type === "client" && <SlabBadge slab={party.type === "b2b" ? party.category : "retail"} />}
                  {ledger && (
                    <span className={`font-mono2 text-sm font-bold ${ledger.balance > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid="party-profile-balance">
                      {inr(Math.abs(ledger.balance))} {ledger.balance > 0 ? (selected.type === "client" ? "Dr" : "Cr") : "settled"}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => openEdit(party, selected.type)} data-testid="party-edit-btn" size="sm"
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
                Edit Profile
              </Button>
              {user?.role === "admin" && (
                <Button onClick={remove} data-testid="party-delete-btn" size="sm" variant="outline"
                  className="border-red-500/50 text-red-600 hover:bg-red-500/10 rounded-full">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              )}
              <button onClick={() => setSelected(null)} data-testid="party-close-btn"
                className="text-slate-500 hover:text-slate-700 p-1.5 rounded-lg hover:bg-slate-100 transition-colors duration-150">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mb-6" data-testid="party-profile-fields">
            <Field label="Contact Person" value={party.contact_person} />
            <Field label="Phone" value={party.phone} />
            <Field label="Email" value={party.email} />
            <Field label="State" value={party.state} />
            <Field label="GSTIN" value={party.gstin} mono />
            <Field label="PAN No" value={party.pan} mono />
            <Field label="Aadhaar No" value={party.aadhaar} mono />
            <Field label="Credit Terms" value={party.credit_days ? `${party.credit_days} days` : "Immediate"} />
            <Field label="Opening Balance" value={party.opening_balance ? inr(party.opening_balance) : "—"} />
            <DocLink label="Aadhaar Doc" path={party.aadhaar_doc} />
            <DocLink label="PAN Doc" path={party.pan_doc} />
          </div>
          <div className="mb-6">
            <Field label="Business Address" value={party.address} />
          </div>

          {party.custom && Object.keys(party.custom).length > 0 && (
            <div className="mb-6 rounded-xl border border-slate-200 bg-white/80 p-4" data-testid="party-profile-custom">
              <p className="text-[10px] font-mono2 uppercase tracking-wider text-slate-500 mb-3">Custom Fields</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Object.entries(party.custom).map(([k, v]) => <Field key={k} label="Custom" value={v} />)}
              </div>
            </div>
          )}

          {ledger && ledger.entries.length > 0 && (
            <div data-testid="party-profile-ledger">
              <p className="text-[10px] font-mono2 uppercase tracking-wider text-slate-500 mb-2">Recent Ledger Activity</p>
              <div className="divide-y divide-slate-200 rounded-xl border border-slate-200 overflow-hidden">
                {ledger.entries.slice(-5).reverse().map((e) => (
                  <div key={e.entry_id} className="flex items-center justify-between px-4 py-2.5 text-sm bg-white/70">
                    <span className="text-slate-700">{e.description}</span>
                    <span className="flex items-center gap-4 font-mono2 text-xs">
                      <span className="text-slate-500">{fdate(e.date)}</span>
                      {e.debit > 0 && <span className="text-red-600">Dr {inr(e.debit)}</span>}
                      {e.credit > 0 && <span className="text-emerald-600">Cr {inr(e.credit)}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl" data-testid="party-edit-dialog">
          <DialogHeader><DialogTitle>Edit {selected?.type === "client" ? "Client" : "Vendor"} Profile</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 max-h-[65vh] overflow-y-auto pr-1">
            {selected?.type === "client" && (
              <>
                <div className="col-span-2 flex gap-2">
                  {["b2b", "b2c"].map((t) => (
                    <button type="button" key={t} onClick={() => setForm({ ...form, type: t })} data-testid={`party-type-${t}`}
                      className={`flex-1 rounded-xl border px-4 py-2 text-sm font-semibold uppercase transition-colors duration-200 ${form.type === t ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-700" : "border-slate-300 text-slate-600"}`}>
                      {t}
                    </button>
                  ))}
                </div>
                {form.type === "b2b" && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Price Category</Label>
                    <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="party-category-select"
                      className="w-full h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900">
                      <option value="shopkeeper">Shopkeeper</option>
                      <option value="dealer">Dealer</option>
                      <option value="distributor">Distributor</option>
                    </select>
                  </div>
                )}
              </>
            )}
            {[
              ["Name *", "name", true], ["GSTIN" + (selected?.type === "vendor" || form.type === "b2b" ? " *" : ""), "gstin", false],
              ["Contact Person", "contact_person", false], ["Phone", "phone", false],
              ["Email", "email", false], ["State", "state", false],
              ["PAN No", "pan", false], ["Aadhaar No", "aadhaar", false],
            ].map(([l, k, req]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{l}</Label>
                <Input required={req} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`party-${k}-input`} className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Credit Terms (days)</Label>
              <Input type="number" min="0" value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: Number(e.target.value) })} data-testid="party-credit-days-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Opening Balance / Old Dues (₹)</Label>
              <Input type="number" step="0.01" min="0" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: Number(e.target.value) })} data-testid="party-opening-balance-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
            </div>
            <FileUploadField label="Current Photo" docType="photo" image value={form.photo}
              onChange={(p) => setForm({ ...form, photo: p })} accept="image/*" testid="party-photo-upload" />
            <FileUploadField label="Aadhaar Document" docType="aadhaar_doc" value={form.aadhaar_doc}
              onChange={(p) => setForm({ ...form, aadhaar_doc: p })} testid="party-aadhaar-doc-upload" />
            <FileUploadField label="PAN Document" docType="pan_doc" value={form.pan_doc}
              onChange={(p) => setForm({ ...form, pan_doc: p })} testid="party-pan-doc-upload" />
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Business Address</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="party-address-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <DynamicFields entity={selected?.type === "client" ? "clients" : "vendors"} values={custom} onChange={(k, v) => setCustom({ ...custom, [k]: v })} />
            <div className="col-span-2 pt-2">
              <Button type="submit" disabled={busy} data-testid="party-save-btn"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
                {busy ? "Saving…" : "Save Profile"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
